import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { IUser } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../models/NameUtils';
import { copyToClipboard } from '../../../models/UiUtils';
import { useDelegatedMeetingUsers } from '../../../queries';
import { useLoadAllUsers } from '../../../queries/User/useLoadAllUsers';
import { AppBarNavigationItemPortalDestinationId } from '../../components/AppBar';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { Portal } from '../../components/Portal';
import { ClipboardIcon } from '../../components/svgs/icons/ClipboardIcon';
import { Combobox, ComboboxContent, ComboboxInput, ComboboxList, ComboboxStatus } from '../../components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemDescription,
	ComboboxAvatarItemTitle,
} from '../../components-v2/Combobox/ComboboxAvatarItem';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { Page } from '../../components-v2/PageLayout';
import { ZoomConnection } from '../../components/settings/ZoomConnection';
import { baseStyleSheet, bs } from '../../styles/styles';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { ScheduleMeeting } from '.';
import { styleSheet } from './styles';
import { useScheduleMeetingSettings } from './useScheduleMeetingSettings';

function _ScheduleMeetingSettings() {
	const id = React.useId();
	const userSession = useUserSession();
	const [selectedUser, setSelectedUser] = React.useState(userSession.user);
	const [userSearchValue, setUserSearchValue] = React.useState(getDisplayName(userSession.user));
	const {
		users,
		isLoading: isLoadingUsers,
		error,
	} = useLoadAllUsers({
		pageSize: 500,
	});
	const activeUsers = React.useMemo(() => {
		if (!users.length) return [userSession.user];

		return users
			.filter((user: IUser) => user.activationStatus === 'ACTIVE')
			.sort((a: IUser, b: IUser) => getDisplayName(a).localeCompare(getDisplayName(b)));
	}, [users, userSession]);
	const isAdmin = userSession.user.groups?.includes('admin') ?? false;
	const { delegatedUsers, hasDelegatedAccess } = useDelegatedMeetingUsers({ currentUserId: userSession.user.id });
	const pickerUsers = isAdmin
		? activeUsers
		: [userSession.user, ...delegatedUsers].sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));
	const canPickUser = isAdmin || hasDelegatedAccess;
	const handleUserSelected = (user: IUser) => {
		if (user) {
			setSelectedUser(user);
		}
	};

	const { isLoadingMeetingLinks, meetingConfigs, onAddMeeting, onDeleteMeeting, onEditMeeting, onCloneMeeting } =
		useScheduleMeetingSettings({
			user: selectedUser,
		});
	const firstPublishedMeeting = meetingConfigs?.filter(config => config.isPublished)?.[0];
	const firstMeetingShortCode = React.useMemo(() => {
		return firstPublishedMeeting?.shortCode ?? null;
	}, [firstPublishedMeeting]);
	const onCopyLandingLinkClick = () => {
		if (firstPublishedMeeting == null) {
			toast.error('Unable to copy to clipboard.');
			return;
		}

		const url = firstPublishedMeeting.link.replace(
			`${firstMeetingShortCode}/${firstPublishedMeeting.vanityPath}`,
			`${firstMeetingShortCode}?landing=true`
		);
		const done = copyToClipboard(url);

		if (done) {
			toast.success(`Copied meetings homepage link to clipboard!`);
			return;
		}
		toast.error('Unable to copy to clipboard.');
	};
	let status: React.ReactNode = '';
	if (isLoadingUsers) {
		status = (
			<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
				<LoadingSpinner type='extra-tiny' />
				Searching Users...
			</div>
		);
	} else if (error) {
		status = 'Error loading users';
	} else if (pickerUsers.length === 0 && userSearchValue) {
		status = `No users found for "${userSearchValue}"`;
	} else if (pickerUsers.length > 0) {
		status = `${pickerUsers.length} User${pickerUsers.length === 1 ? '' : 's'} found`;
	} else if (!userSearchValue) {
		status = 'Start typing to search users...';
	}
	return (
		<Page styles={[styleSheet.container]}>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<NavigationBreadcrumbsBar currentLocationName='Schedule a Meeting' />
			</Portal>
			{firstPublishedMeeting ? (
				<div className={css(styleSheet.headerContainer)}>
					<div className={css(styleSheet.headerLeft)}>
						<div className={css(styleSheet.text)}>
							Want to give your clients the option to choose from all your meeting types? Simply copy and send this
							link.
						</div>
						<button
							className={css(baseStyleSheet.ctaButtonSmall, styleSheet.copyLinkButton)}
							onClick={onCopyLandingLinkClick}
						>
							<span>
								<ClipboardIcon className={css(styleSheet.clipboardIcon)} fillColor='#fff' />
								Copy Booking Calendar
							</span>
						</button>
					</div>
					{canPickUser && (
						<div className={css(styleSheet.headerRight)}>
							<Combobox
								items={pickerUsers}
								value={selectedUser}
								onValueChange={handleUserSelected}
								inputValue={userSearchValue}
								onInputValueChange={setUserSearchValue}
								itemToStringLabel={(user: IUser) => getDisplayName(user)}
								isItemEqualToValue={(item: IUser, value: IUser) => item.id === value.id}
							>
								<div className={css(styleSheet.userSearchContainer)}>
									<label htmlFor={`${id}-user-combobox`} className={css(styleSheet.userSearchLabel)}>
										Show Meeting Links For:
									</label>
									<ComboboxInput placeholder='Search user...' id={`${id}-user-combobox`} styles={[bs.w80]} />
								</div>
								<ComboboxContent>
									<ComboboxStatus>{status}</ComboboxStatus>
									<ComboboxList>
										{(user: IUser) => (
											<ComboboxAvatarItem key={user.id} value={user}>
												<ComboboxAvatarItemAvatar
													imageUrl={user.profilePic}
													fallbackText={user.firstName?.substring(0, 1) + user.lastName?.substring(0, 1)}
												/>
												<ComboboxAvatarItemTitle>{getDisplayName(user)}</ComboboxAvatarItemTitle>
												<ComboboxAvatarItemDescription>{user.email}</ComboboxAvatarItemDescription>
											</ComboboxAvatarItem>
										)}
									</ComboboxList>
								</ComboboxContent>
							</Combobox>
							{isAdmin && (
								<NavLink to='/schedule-meeting/team-links' className={css(styleSheet.teamLinksLink)}>
									Manage Team Meeting Links
								</NavLink>
							)}
						</div>
					)}
				</div>
			) : null}
			{userSession.account.features.zoom?.enabled ? (
				<ZoomConnection
					connectReturnPath='/schedule-meeting'
					hideWhenConnected
					dismissible
					containerStyles={[bs.mb4]}
				/>
			) : null}
			<ScheduleMeeting
				isLoadingMeetingLinks={isLoadingMeetingLinks}
				meetingConfigs={meetingConfigs}
				onAddMeeting={onAddMeeting}
				onDeleteMeeting={onDeleteMeeting}
				onEditMeeting={onEditMeeting}
				onCloneMeeting={onCloneMeeting}
			/>
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</Page>
	);
}

export const ScheduleMeetingSettingsPage = observer(_ScheduleMeetingSettings);
